/**
 * A data table, on a phone.
 *
 * The portal's tables carry six to nine columns. On a 390px screen that is a horizontal
 * scrollbar, and a horizontal scrollbar is not a compromise — it is a way of hiding the
 * column that matters behind a gesture nobody makes. The flagged badge, the amount, who
 * has the thing: all of them end up off-screen right, and the person scrolls the page
 * vertically past a row whose whole point was three columns to the right.
 *
 * So below `lg` the same rows become cards: title, a line under it, the badges that
 * change what you do next, and a tap for everything else. Nothing is dropped — the
 * fields the card cannot fit are in the sheet, labelled, in the order the table had
 * them.
 *
 * Deliberately one component rather than a card layout per page. Ten hand-written
 * mobile layouts drift: one forgets the badge, one loses a column when somebody adds it
 * to the table, and by the third month the phone shows different information from the
 * desktop. Here a page declares its fields once and both views read the same list.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '@/i18n';

import { Modal } from './Modal';
import { Card } from './ui';

export interface MobileField<T> {
  label: string;
  value: (row: T) => ReactNode;
}

export interface MobileListProps<T> {
  rows: T[];
  keyOf: (row: T) => string;
  /** The line somebody scans down. */
  title: (row: T) => ReactNode;
  /** Under it, quieter: a code, a date, a site. */
  subtitle?: (row: T) => ReactNode;
  /** Status and exceptions — the things that change what you do next. */
  badges?: (row: T) => ReactNode;
  /** The right-hand end of the card: an amount, a duration, a count. */
  trailing?: (row: T) => ReactNode;
  /** Everything else, shown in the sheet. */
  fields: MobileField<T>[];
  /** What the sheet offers to do. Rendered in its footer. */
  action?: (row: T) => ReactNode;
}

export function MobileList<T>({
  rows,
  keyOf,
  title,
  subtitle,
  badges,
  trailing,
  fields,
  action,
}: MobileListProps<T>) {
  const { t } = useI18n();
  const [open, setOpen] = useState<T | null>(null);

  return (
    <>
      <ul className="lg:hidden space-y-2">
        {rows.map((row) => (
          <li key={keyOf(row)}>
            {/*
              The whole card is the target. A small "details" link on a card that is
              already the size of a thumb is a smaller target for no reason, and people
              tap the card anyway and conclude it is broken.
            */}
            <button
              type="button"
              onClick={() => setOpen(row)}
              className="w-full text-start"
              aria-label={t('table.openRow')}
            >
              <Card className="flex items-start gap-3 active:bg-surface-alt transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{title(row)}</div>
                  {subtitle ? (
                    <div className="text-xs text-subtle truncate mt-0.5">{subtitle(row)}</div>
                  ) : null}
                  {badges ? (
                    <div className="flex flex-wrap items-center gap-2 mt-2 empty:hidden">
                      {badges(row)}
                    </div>
                  ) : null}
                </div>

                {trailing ? (
                  <div className="text-sm font-semibold tabular shrink-0 text-end">
                    {trailing(row)}
                  </div>
                ) : null}

                <svg
                  viewBox="0 0 20 20"
                  className="w-4 h-4 shrink-0 text-subtle mt-0.5 rtl:-scale-x-100"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M7.5 4.5 13 10l-5.5 5.5" />
                </svg>
              </Card>
            </button>
          </li>
        ))}
      </ul>

      <Modal
        open={open !== null}
        onClose={() => setOpen(null)}
        size="sm"
        title={open ? String(reactText(title(open))) : ''}
        description={open && subtitle ? String(reactText(subtitle(open))) : undefined}
        footer={
          open && action ? (
            <>
              {action(open)}
              <button type="button" className="btn-secondary" onClick={() => setOpen(null)}>
                {t('common.close')}
              </button>
            </>
          ) : (
            <button type="button" className="btn-secondary" onClick={() => setOpen(null)}>
              {t('common.close')}
            </button>
          )
        }
      >
        {open ? (
          <dl className="space-y-0">
            {badges ? (
              <div className="flex flex-wrap items-center gap-2 pb-3 empty:hidden">
                {badges(open)}
              </div>
            ) : null}

            {fields.map((field) => {
              const value = field.value(open);
              // A row that would say "Site: —" is a row that costs a line and answers
              // nothing. The desktop table needs the column to keep its grid; a sheet
              // does not.
              if (value === null || value === undefined || value === '') return null;

              return (
                <div
                  key={field.label}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-border last:border-0"
                >
                  <dt className="text-sm text-muted sm:w-36 sm:shrink-0">{field.label}</dt>
                  <dd className="text-sm flex-1 min-w-0">{value}</dd>
                </div>
              );
            })}
          </dl>
        ) : null}
      </Modal>
    </>
  );
}

/**
 * A dialog title has to be a string — it is announced, and it is the `aria-labelledby`
 * target. `title` may legitimately return a node (a name next to a badge), so take the
 * text out of it rather than forcing every caller to supply the same thing twice.
 */
function reactText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactText).join(' ');
  if (typeof node === 'object' && 'props' in node) {
    return reactText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}
